import React, { useEffect, useState } from "react";
import AssessmentSection from "../components/AssessmentSection";
import { useNavigate, useParams } from "react-router-dom";
import { getAssessment, getWarmup } from "../services/AssessmentService";
import { openAssignedContent } from "../services/AssignmentService";
import AppShell from "../components/ui/AppShell";
import { Card, EmptyState, TextLink } from "../components/ui";
import { Quiz } from "../components/icons";

/**
 * One assessment on a page of its own.
 *
 * Reached two ways, and `:id` means something different in each:
 *
 * - Email invitation, `/assessment/:id/:type/:token` — the backend builds this
 *   link with the assessment's MODULE id, so it is looked up by module.
 * - From inside the app, `/assessment/:id/:type` — the learner is already
 *   signed in and `:id` is the assessment's OWN id. This is how assessments
 *   with no host PPT lesson (standalone, or whose host lesson was deleted) are
 *   opened from a learning path, since they have no section workspace to
 *   deep-link into.
 *
 * The in-app entry is a FIRST-CLASS destination, not a fallback: it carries the
 * same shell as every other learner screen, and it always renders something.
 * It previously gated the whole page on the fetched assessment having a name,
 * so a failed or empty fetch painted a blank white screen — the learner clicked
 * "Start" and, as far as they could tell, nothing happened. Loading and failure
 * are now states you can see, with a way back out of both.
 */
function IndividualAssessment() {
  const { id, type, token } = useParams();
  const navigate = useNavigate();
  const [module, setModule] = useState(null);
  const [state, setState] = useState("loading"); // loading | ready | error
  const [showAssessment, setShowAssessment] = useState(false);

  // Only the invitation link identifies content by module; without a token the
  // id is the assessment itself, so we must not resolve it by module.
  const byModule = !!token;
  // The invitation link is opened from an email, outside the app — there is no
  // session behind it to wrap in the learner shell.
  const inApp = !token;

  useEffect(() => {
    (async () => {
      setState("loading");
      setModule(null);
      try {
        if (!id) {
          setState("error");
          return;
        }
        if (token) localStorage.setItem("token", token);

        let response;
        if (type === "warmup") {
          response = await getWarmup(id);
        } else if (byModule) {
          response = await getAssessment(id);
        } else {
          // Opened from the learner's plan: fetch it on the strength of the
          // assignment, not the learner's browse scope, so an assigned
          // assessment is never unreachable.
          const payload = await openAssignedContent("assessment", id);
          response = payload?.assessment || null;
        }

        if (!response?.name) {
          setState("error");
          return;
        }

        setModule({
          ...response,
          title: response.name,
          // The lesson-scoped viewer keys off `_id`; for a module-resolved
          // assessment that is the module it belongs to.
          ...(type === "assessment" && byModule ? { _id: response.module } : {}),
        });
        setState("ready");
      } catch (error) {
        console.log(error);
        setState("error");
      }
    })();
  }, [id, type, token, byModule]);

  const body =
    state === "loading" ? (
      <p className="ds-sub">Loading this assessment…</p>
    ) : state === "error" ? (
      <Card>
        <EmptyState
          icon={Quiz}
          text="This assessment could not be loaded. It may have been unpublished, or it is no longer assigned to you — please contact your training team."
          action={
            inApp ? (
              <TextLink onClick={() => navigate("/my-learning")}>
                Back to My Learning Plan
              </TextLink>
            ) : null
          }
        />
      </Card>
    ) : (
      <AssessmentSection
        selectedModule={module}
        type={type}
        assessmentId={byModule ? undefined : id}
        hideNavigation={"yes"}
        goBack={setShowAssessment}
      />
    );

  // Emailed invitations land here with no session and no plan to go back to, so
  // they keep the bare page they have always had.
  if (!inApp) return <div>{body}</div>;

  return (
    <AppShell crumb={module?.name || "Assessment"} active="plan">
      <div className="ds-section__head" style={{ marginTop: 0 }}>
        <div>
          <h1 className="ds-h1">{module?.name || "Assessment"}</h1>
          <p className="ds-sub">
            Assigned to you — this one stands on its own, outside any module.
          </p>
        </div>
        <TextLink onClick={() => navigate("/my-learning")}>
          Back to My Learning Plan
        </TextLink>
      </div>
      {body}
    </AppShell>
  );
}

export default IndividualAssessment;
